/*
  hc-collection-type-tabs.js — Full AJAX via Shopify Section Rendering API.
  Fetches paginated product HTML from sections/hc-ctt-products-fragment.liquid
  (which renders the standard product-grid-item snippet), appends items into
  the grid, then builds tabs from unique data-product-type values.
  Using Section Rendering preserves visual parity with standard collection pages
  (compare-at price, promo code metafields, badges, hover images, etc.).
*/
(function () {
  'use strict';

  var ALL_TYPE = '__all__';
  var MAX_PAGES = 25;
  var PARALLEL_BATCH = 8;

  function toArray(nl) { return Array.prototype.slice.call(nl); }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function fetchFragmentPage(handle, fragmentId, page) {
    var url = '/collections/' + encodeURIComponent(handle)
      + '?section_id=' + encodeURIComponent(fragmentId)
      + '&page=' + page;
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.text();
    });
  }

  function parseItems(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return toArray(doc.querySelectorAll('[data-hc-ctt-item]'));
  }

  function fetchAllItems(handle, fragmentId) {
    /*
      Fires PARALLEL_BATCH page requests at once. If the last page in a batch
      is full (50 items), the next batch fires — otherwise stop. Preserves
      collection sort order by concatenating in page order and breaking on
      the first empty page encountered.
    */
    var items = [];

    function runBatch(startPage) {
      var pagePromises = [];
      for (var p = startPage; p < startPage + PARALLEL_BATCH && p <= MAX_PAGES; p++) {
        (function (page) {
          pagePromises.push(
            fetchFragmentPage(handle, fragmentId, page)
              .then(parseItems)
              .catch(function () { return []; })
          );
        })(p);
      }
      return Promise.all(pagePromises).then(function (pages) {
        var stopBatching = false;
        var lastSize = 0;
        for (var i = 0; i < pages.length; i++) {
          if (pages[i].length === 0) { stopBatching = true; break; }
          items = items.concat(pages[i]);
          lastSize = pages[i].length;
        }
        var nextStart = startPage + PARALLEL_BATCH;
        if (!stopBatching && lastSize === 50 && nextStart <= MAX_PAGES) {
          return runBatch(nextStart);
        }
        return items;
      });
    }

    return runBatch(1);
  }

  function parseLabelMap(raw) {
    var map = {};
    if (!raw) return map;
    raw.split(/\r?\n/).forEach(function (line) {
      var idx = line.indexOf('=');
      if (idx <= 0) return;
      var key = line.slice(0, idx).trim();
      var val = line.slice(idx + 1).trim();
      if (key && val) map[key] = val;
    });
    return map;
  }

  /*
    Normalize a type string for fuzzy lookups (merge map). Canonicalizes dashes
    and whitespace so "Black Interior -Leather Handbag", "Black Interior - Leather
    Handbag", and "Black Interior-Leather Handbag" all match the same key.
  */
  function normalizeKey(s) {
    return String(s || '')
      .replace(/[\u2013\u2014\u2212]/g, '-')
      .replace(/\s*-\s*/g, ' - ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function parseMergeMap(raw) {
    var map = {};
    if (!raw) return map;
    raw.split(/\r?\n/).forEach(function (line) {
      var idx = line.indexOf('=');
      if (idx <= 0) return;
      var key = line.slice(0, idx).trim();
      var val = line.slice(idx + 1).trim();
      if (key && val) map[normalizeKey(key)] = val;
    });
    return map;
  }

  /*
    Tag stages: ordered list of {label, tag, key} parsed from textarea where
    each line is "Display Label = tag-name". Order is preserved (admin controls
    tab order). `key` is the normalized tag used for case-insensitive matching
    against the product's data-product-tags attribute.
  */
  function parseTagStages(raw) {
    var stages = [];
    if (!raw) return stages;
    raw.split(/\r?\n/).forEach(function (line) {
      var idx = line.indexOf('=');
      if (idx <= 0) return;
      var label = line.slice(0, idx).trim();
      var tag = line.slice(idx + 1).trim();
      if (label && tag) {
        stages.push({ label: label, tag: tag, key: normalizeKey(tag) });
      }
    });
    return stages;
  }

  function itemHasTag(item, key) {
    var raw = item.getAttribute('data-product-tags') || '';
    if (!raw) return false;
    var tags = raw.split('|');
    for (var i = 0; i < tags.length; i++) {
      if (normalizeKey(tags[i]) === key) return true;
    }
    return false;
  }

  function buildUniqueTypes(items, groupFn) {
    var seen = {};
    var ordered = [];
    for (var i = 0; i < items.length; i++) {
      var t = items[i].getAttribute('data-product-type');
      if (!t) continue;
      var g = groupFn ? groupFn(t) : t;
      if (seen[g]) continue;
      seen[g] = true;
      ordered.push(g);
    }
    return ordered;
  }

  /*
    Tag-mode tab list. Two behaviors fused:
    1. Tab ORDER follows collection product order — we add a stage the first
       time we encounter a matching product (mirrors buildUniqueTypes for the
       product_type mode). Admins reorder tabs by reordering the Shopify
       collection sort, not by editing the textarea.
    2. Empty stages auto-hide (Matrixify draft → product missing from feed →
       stage never matched → stage not added).
  */
  function buildActiveStages(items, stages) {
    var seen = {};
    var ordered = [];
    for (var j = 0; j < items.length && ordered.length < stages.length; j++) {
      for (var i = 0; i < stages.length; i++) {
        var stage = stages[i];
        if (seen[stage.tag]) continue;
        if (itemHasTag(items[j], stage.key)) {
          seen[stage.tag] = true;
          ordered.push(stage);
          break;
        }
      }
    }
    return ordered;
  }

  function init(root) {
    if (root.dataset.hcCttInit === '1') return;
    root.dataset.hcCttInit = '1';

    var handle = root.getAttribute('data-hc-ctt-handle');
    var fragmentId = root.getAttribute('data-hc-ctt-fragment-id') || 'hc-ctt-products-fragment';
    var batch = parseInt(root.getAttribute('data-hc-ctt-per-page'), 10) || 24;
    var showAll = root.getAttribute('data-hc-ctt-show-all-tab') === 'true';
    var allLabel = root.getAttribute('data-hc-ctt-all-label') || 'All';
    var stripLabel = root.getAttribute('data-hc-ctt-strip-label') || '';
    var labelMap = parseLabelMap(root.getAttribute('data-hc-ctt-label-map') || '');
    var mergeMap = parseMergeMap(root.getAttribute('data-hc-ctt-merge-map') || '');
    var groupBy = root.getAttribute('data-hc-ctt-group-by') || 'product_type';
    var tagStages = parseTagStages(root.getAttribute('data-hc-ctt-tag-stages') || '');
    var isTagMode = groupBy === 'tag' && tagStages.length > 0;

    // Resolve a raw product_type to its display group (for tabs + filtering).
    // Uses fuzzy key (normalized dashes/whitespace/case) so merge rules tolerate
    // typos like missing/extra space around dashes.
    function groupOf(type) {
      if (!type) return type;
      return mergeMap[normalizeKey(type)] || type;
    }

    // In tag mode the tab "type" IS the stage tag itself; label comes from
    // the parsed stages array. In product-type mode, fall back to label_map /
    // strip_from_labels rules.
    function displayLabel(type) {
      if (isTagMode) {
        for (var i = 0; i < tagStages.length; i++) {
          if (tagStages[i].tag === type) return tagStages[i].label;
        }
        return type;
      }
      if (labelMap[type]) return labelMap[type];
      if (stripLabel) {
        var stripped = type.split(stripLabel).join('').replace(/\s+/g, ' ').trim();
        if (stripped) return stripped;
      }
      return type;
    }

    var tabsEl = root.querySelector('[data-hc-ctt-tabs]');
    var gridEl = root.querySelector('[data-hc-ctt-grid]');
    var footerEl = root.querySelector('[data-hc-ctt-footer]');
    var loadMoreBtn = root.querySelector('[data-hc-ctt-load-more]');
    var loadingEl = root.querySelector('[data-hc-ctt-loading]');
    var emptyEl = root.querySelector('[data-hc-ctt-empty]');

    function show(el, visible) {
      if (!el) return;
      if (visible) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    }

    if (!handle) {
      show(loadingEl, false);
      show(emptyEl, true);
      return;
    }

    var allItems = [];
    var types = [];
    var state = {};
    var activeType = ALL_TYPE;

    function pillHtml(type, label, isActive) {
      return (
        '<button type="button" class="hc-collection-type-tabs__pill' + (isActive ? ' is-active' : '') + '"' +
        ' role="tab" aria-selected="' + (isActive ? 'true' : 'false') + '"' +
        ' data-hc-ctt-pill data-type="' + escapeHtml(type) + '">' +
        escapeHtml(label) + '</button>'
      );
    }

    function renderTabs() {
      var html = '';
      if (showAll) html += pillHtml(ALL_TYPE, allLabel, activeType === ALL_TYPE);
      for (var i = 0; i < types.length; i++) {
        html += pillHtml(types[i], displayLabel(types[i]), activeType === types[i]);
      }
      tabsEl.innerHTML = html;
      toArray(tabsEl.querySelectorAll('[data-hc-ctt-pill]')).forEach(function (pill) {
        pill.addEventListener('click', function () {
          var type = pill.dataset.type;
          if (type === activeType) return;
          activeType = type;
          if (!state[type]) state[type] = batch;
          renderTabs();
          applyFilter();
          updateLoadMore();
          scrollActivePillIntoView();
        });
      });
      updateBarScrollHint();
    }

    function updateBarScrollHint() {
      if (!tabsEl) return;
      var atEnd = tabsEl.scrollLeft + tabsEl.clientWidth >= tabsEl.scrollWidth - 2;
      tabsEl.classList.toggle('is-scroll-end', atEnd);
    }

    function scrollActivePillIntoView() {
      if (!tabsEl) return;
      var active = tabsEl.querySelector('.hc-collection-type-tabs__pill.is-active');
      if (!active) return;
      var pillLeft = active.offsetLeft;
      var pillRight = pillLeft + active.offsetWidth;
      var barScroll = tabsEl.scrollLeft;
      var barWidth = tabsEl.clientWidth;
      if (pillLeft < barScroll + 12) {
        tabsEl.scrollTo({ left: Math.max(0, pillLeft - 12), behavior: 'smooth' });
      } else if (pillRight > barScroll + barWidth - 12) {
        tabsEl.scrollTo({ left: pillRight - barWidth + 12, behavior: 'smooth' });
      }
    }

    function matches(item, type) {
      if (type === ALL_TYPE) return true;
      if (isTagMode) {
        // type === the stage tag itself; lookup normalized key for comparison
        for (var i = 0; i < tagStages.length; i++) {
          if (tagStages[i].tag === type) return itemHasTag(item, tagStages[i].key);
        }
        return false;
      }
      return groupOf(item.getAttribute('data-product-type')) === type;
    }

    function countMatches(type) {
      var n = 0;
      for (var i = 0; i < allItems.length; i++) if (matches(allItems[i], type)) n++;
      return n;
    }

    function applyFilter() {
      var cap = state[activeType] || batch;
      var shown = 0;
      for (var i = 0; i < allItems.length; i++) {
        var item = allItems[i];
        if (matches(item, activeType) && shown < cap) {
          item.removeAttribute('hidden');
          shown++;
        } else {
          item.setAttribute('hidden', '');
        }
      }
    }

    function updateLoadMore() {
      if (!footerEl) return;
      var total = countMatches(activeType);
      var cap = state[activeType] || batch;
      show(footerEl, cap < total);
    }

    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', function () {
        state[activeType] = (state[activeType] || batch) + batch;
        applyFilter();
        updateLoadMore();
      });
    }

    fetchAllItems(handle, fragmentId).then(function (items) {
      show(loadingEl, false);

      // Tag mode: keep every item with at least one tag (product_type optional).
      // Type mode: keep only items that have a product_type (original behavior).
      if (isTagMode) {
        allItems = items.filter(function (it) { return !!it.getAttribute('data-product-tags'); });
      } else {
        allItems = items.filter(function (it) { return !!it.getAttribute('data-product-type'); });
      }

      if (!allItems.length) {
        show(emptyEl, true);
        return;
      }

      if (isTagMode) {
        var activeStages = buildActiveStages(allItems, tagStages);
        types = activeStages.map(function (s) { return s.tag; });
      } else {
        types = buildUniqueTypes(allItems, groupOf);
      }
      activeType = showAll ? ALL_TYPE : (types[0] || ALL_TYPE);
      state[activeType] = batch;

      var frag = document.createDocumentFragment();
      for (var i = 0; i < allItems.length; i++) frag.appendChild(allItems[i]);
      gridEl.appendChild(frag);

      renderTabs();
      applyFilter();
      updateLoadMore();

      if (tabsEl) {
        tabsEl.addEventListener('scroll', updateBarScrollHint, { passive: true });
        window.addEventListener('resize', updateBarScrollHint, { passive: true });
        updateBarScrollHint();
      }
    }).catch(function (err) {
      show(loadingEl, false);
      show(emptyEl, true);
      if (window.console && console.warn) console.warn('[hc-ctt] fetch failed:', err);
    });
  }

  function boot(scope) {
    var roots = (scope || document).querySelectorAll('[data-hc-ctt]');
    toArray(roots).forEach(function (root) {
      try { init(root); } catch (e) { if (window.console) console.warn('[hc-ctt]', e); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e && e.target) boot(e.target);
  });
})();
